import React, { useContext, useEffect, useRef, useState } from "react";
import Lottie from "lottie-web";
import arrayMove from "array-move";
import MyContext from "../../context/MyContext";
import ProjectContext from "../../context/ProjectContext";
import { editLayerMotion } from "./MotionOverlay";
import { hasMotion } from "../../Utils/layerMotion";

// Right-click menu for any layer on the canvas (characters, props, pictures, videos, text, shapes,
// animated text): quick actions without looking for toolbars or panels.

const ID_PREFIXES = ["Inner-", "Outer-", "Text-"];

function layerIdFromTarget(target, canvas) {
  let el = target;
  while (el && el !== canvas) {
    const id = el.id || "";
    const prefix = ID_PREFIXES.find((p) => id.startsWith(p));
    if (prefix) return id.slice(prefix.length);
    el = el.parentElement;
  }
  return null;
}

// The layer under the mouse. A selected layer is covered by its resize frame (no layer id), so
// when the clicked element is not part of a layer, look through everything under the pointer,
// then at the layers' boxes (topmost first).
function layerAt(e, canvas, layers) {
  const byId = (id) => id && layers.find((l) => String(l.AnimationId) === id);
  let layer = byId(layerIdFromTarget(e.target, canvas));
  if (layer) return layer;
  if (document.elementsFromPoint) {
    for (const el of document.elementsFromPoint(e.clientX, e.clientY)) {
      if (!canvas.contains(el)) continue;
      layer = byId(layerIdFromTarget(el, canvas));
      if (layer) return layer;
    }
  }
  for (let i = layers.length - 1; i >= 0; i--) {
    const l = layers[i];
    if (!l || l.type === "audio") continue;
    const el = document.getElementById("Outer-" + l.AnimationId);
    if (!el || el.offsetParent === null) continue;
    const r = el.getBoundingClientRect();
    if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) return l;
  }
  return null;
}

const CanvasContextMenu = ({ canvasRef }) => {
  const {
    LayersData,
    CanvasDisplay,
    clone,
    UndoLayer,
    setUndoLayer,
    setRedoLayer,
    seteditableObject,
    ChangeCount,
    setChangeCount,
  } = useContext(MyContext);
  const { setLayersData, slides } = useContext(ProjectContext);
  const [menu, setMenu] = useState(null); // { x, y, key }
  const layersRef = useRef(LayersData);
  layersRef.current = LayersData;
  const menuRef = useRef(null);

  // Open on right-click over a layer.
  useEffect(() => {
    const canvas = canvasRef && canvasRef.current;
    if (!canvas) return undefined;
    const onContextMenu = (e) => {
      if (!CanvasDisplay) return; // not while playing
      const layer = layerAt(e, canvas, layersRef.current);
      if (!layer || layer.type === "audio") return;
      e.preventDefault();
      setMenu({ x: e.clientX, y: e.clientY, key: layer.key });
    };
    canvas.addEventListener("contextmenu", onContextMenu);
    return () => canvas.removeEventListener("contextmenu", onContextMenu);
  }, [canvasRef, CanvasDisplay]);

  // Close on click elsewhere, Escape, scroll or resize.
  useEffect(() => {
    if (!menu) return undefined;
    const close = (e) => {
      if (e && e.type === "mousedown" && menuRef.current && menuRef.current.contains(e.target)) return;
      setMenu(null);
    };
    const onKey = (e) => e.key === "Escape" && setMenu(null);
    window.addEventListener("mousedown", close, true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", close);
    window.addEventListener("wheel", close, { passive: true });
    return () => {
      window.removeEventListener("mousedown", close, true);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", close);
      window.removeEventListener("wheel", close);
    };
  }, [menu]);

  // Keep the menu on screen.
  const [pos, setPos] = useState(null);
  useEffect(() => {
    if (!menu || !menuRef.current) {
      setPos(null);
      return;
    }
    const r = menuRef.current.getBoundingClientRect();
    setPos({
      left: Math.max(8, Math.min(menu.x, window.innerWidth - r.width - 8)),
      top: Math.max(8, Math.min(menu.y, window.innerHeight - r.height - 8)),
    });
  }, [menu]);

  if (!menu) return null;
  const layer = LayersData.find((l) => l.key === menu.key);
  if (!layer) return null;
  const index = LayersData.indexOf(layer);
  const last = LayersData.length - 1;

  const copy = (o) => (clone ? clone(o) : JSON.parse(JSON.stringify(o)));

  // Same undo stack as Ctrl+Z (last 10 steps).
  const remember = () => {
    const stack = [...(UndoLayer || [])];
    stack.push([...LayersData]);
    if (stack.length > 10) stack.shift();
    if (setUndoLayer) setUndoLayer(stack);
    if (setRedoLayer) setRedoLayer([]);
  };
  const changed = () => setChangeCount && setChangeCount((ChangeCount || 0) + 1);
  const run = (fn) => () => {
    setMenu(null);
    fn();
  };

  const updateLayer = (change) => {
    remember();
    const array = LayersData.map((l) => (l.key === layer.key ? change(copy(l)) || l : l));
    setLayersData(array);
    changed();
  };

  const select = (array, key) =>
    array.map((l) =>
      l.key === key
        ? { ...l, Editable: true, showDiv: true, toolbar: false, FurtherSetting: false }
        : l.Editable || l.showDiv || l.toolbar
        ? { ...l, Editable: false, showDiv: false, toolbar: false }
        : l
    );

  const openSettings = () => {
    setLayersData(select(LayersData, layer.key));
    if (seteditableObject) seteditableObject({ showPanel: true, editableItem: layer });
  };

  const moveAtoZ = () => {
    // Open the path editor once the menu has closed.
    setTimeout(() => editLayerMotion(layer.key), 0);
  };

  const duplicate = () => {
    remember();
    const obj = copy(layer);
    const usedIds = new Set();
    (slides || []).forEach((s) => (s.IsActive ? LayersData : s.LayerData || []).forEach((l) => l && usedIds.add(l.AnimationId)));
    LayersData.forEach((l) => usedIds.add(l.AnimationId));
    let newId;
    do {
      newId = Math.random().toString(36).slice(2, 10);
    } while (usedIds.has(newId));
    obj.key = LayersData.reduce((m, l) => (typeof l.key === "number" && l.key > m ? l.key : m), 0) + 1;
    obj.AnimationId = newId;
    obj.Name = (obj.Name || obj.type) + "-copy";
    obj.layerIndex = LayersData.reduce((m, l) => (typeof l.layerIndex === "number" && l.layerIndex > m ? l.layerIndex : m), 0) + 1;
    obj.Added = true;
    if (typeof obj.x === "number") obj.x += 20;
    if (typeof obj.y === "number") obj.y += 20;
    if (typeof obj.originalX === "number") obj.originalX = obj.x;
    if (typeof obj.originalY === "number") obj.originalY = obj.y;
    setLayersData(select(LayersData.concat(obj), obj.key));
    changed();
  };

  const arrange = (to) => {
    if (to === index || to < 0 || to > last) return;
    remember();
    setLayersData(arrayMove(LayersData, index, to));
    changed();
  };

  const flip = () =>
    updateLayer((l) => {
      l.flip = l.flip === "scaleX(-1)" ? "scaleX(1)" : "scaleX(-1)";
      return l;
    });

  const toggleLock = () =>
    updateLayer((l) => {
      l.lock = !l.lock;
      l.layerlock = true;
      l.Editable = false;
      l.toolbar = false;
      return l;
    });

  const remove = () => {
    remember();
    try {
      Lottie.destroy(layer.AnimationId);
    } catch (e) {
      /* not a Lottie layer */
    }
    setLayersData(
      LayersData.filter((l) => l.key !== layer.key).map((l) => {
        l.Added = true;
        return l;
      })
    );
    if (seteditableObject) seteditableObject({ showPanel: false, editableItem: [] });
    changed();
  };

  const removeMove = () =>
    updateLayer((l) => {
      delete l.Motion;
      return l;
    });

  const moving = hasMotion(layer);
  const items = [
    { icon: "moving", label: moving ? "Edit move (A → Z)…" : "Move from A to Z…", onClick: moveAtoZ, hidden: layer.lock, strong: true },
    { icon: "block", label: "Remove move", onClick: removeMove, hidden: !moving || layer.lock },
    { divider: true },
    { icon: "tune", label: "Settings…", onClick: openSettings, hidden: layer.lock },
    { icon: "content_copy", label: "Duplicate", onClick: duplicate },
    { icon: "flip", label: "Flip horizontally", onClick: flip, hidden: layer.lock },
    { divider: true },
    { icon: "flip_to_front", label: "Bring to front", onClick: () => arrange(last), disabled: index === last },
    { icon: "arrow_upward", label: "Bring forward", onClick: () => arrange(index + 1), disabled: index === last },
    { icon: "arrow_downward", label: "Send backward", onClick: () => arrange(index - 1), disabled: index === 0 },
    { icon: "flip_to_back", label: "Send to back", onClick: () => arrange(0), disabled: index === 0 },
    { divider: true },
    { icon: layer.lock ? "lock_open" : "lock", label: layer.lock ? "Unlock" : "Lock", onClick: toggleLock },
    { icon: "delete", label: "Delete", onClick: remove, danger: true },
  ].filter((i) => !i.hidden);

  return (
    <div
      ref={menuRef}
      role="menu"
      className="CanvasContextMenu"
      onContextMenu={(e) => e.preventDefault()}
      style={{
        position: "fixed",
        left: pos ? pos.left : menu.x,
        top: pos ? pos.top : menu.y,
        visibility: pos ? "visible" : "hidden",
        zIndex: 10000,
        minWidth: 210,
        background: "#fff",
        borderRadius: 8,
        boxShadow: "0 6px 24px rgba(0,0,0,.25)",
        padding: "6px 0",
        fontFamily: "sans-serif",
        fontSize: 13,
        color: "#1d2433",
        userSelect: "none",
      }}
    >
      <div style={{ padding: "4px 14px 6px", fontSize: 11, color: "#7a8396", textTransform: "uppercase", letterSpacing: 0.4 }}>
        {layer.Name || layer.type}
      </div>
      {items.map((item, i) =>
        item.divider ? (
          <div key={"d" + i} style={{ height: 1, background: "#e7ebf3", margin: "4px 0" }} />
        ) : (
          <div
            key={item.label}
            role="menuitem"
            onClick={item.disabled ? undefined : run(item.onClick)}
            onMouseEnter={(e) => !item.disabled && (e.currentTarget.style.background = "#eef3ff")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "6px 14px",
              cursor: item.disabled ? "default" : "pointer",
              opacity: item.disabled ? 0.4 : 1,
              color: item.danger ? "#c62828" : undefined,
              fontWeight: item.strong ? 600 : 400,
            }}
          >
            <span className="material-icons" style={{ fontSize: 18, width: 18, overflow: "hidden", flexShrink: 0, color: item.danger ? "#c62828" : item.strong ? "#296AFF" : "#5b6476" }}>
              {item.icon}
            </span>
            {item.label}
          </div>
        )
      )}
    </div>
  );
};

export default CanvasContextMenu;